import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Observable, catchError, EMPTY } from 'rxjs'
import { LogService } from 'htv-lib'
import { environment } from '../environments/environment'
import { LocationService } from './location.service'
import { HttpUtil } from '../utils/http-util'
import { MessageDto } from '../interfaces/message'
import { StateDataService } from './state-data.service'

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  readonly CMN_LOCATION_API_URL = environment.cmnLocationApiUrl

  constructor(
    private httpClient: HttpClient,
    private logService: LogService,
    private stateDataService: StateDataService,
    private locationService: LocationService
  ) {
    // do nothing
  }

  getMessages(): Observable<MessageDto[]> {
    const locationId = this.locationService.location?.id
    if (!locationId) {
      this.logService.error('[MessageService] No location ID available')
      return EMPTY
    }

    const url = `${this.CMN_LOCATION_API_URL}/public/locations/${locationId}/messages`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))

    return this.httpClient.get<MessageDto[]>(url, { headers }).pipe(
      catchError(err => {
        this.logService.error(`[MessageService] Could not retrieve messages for location ${locationId}`)
        this.logService.debug(err.message)
        return EMPTY
      })
    )
  }

  markAsDelivered(messageId: string): Observable<MessageDto> {
    const locationId = this.locationService.location?.id
    if (!locationId) {
      this.logService.error('[MessageService] No location ID available')
      return EMPTY
    }

    const url = `${this.CMN_LOCATION_API_URL}/public/locations/${locationId}/messages/${messageId}/mark-delivered`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))

    return this.httpClient.put<MessageDto>(url, {}, { headers }).pipe(
      catchError(err => {
        this.logService.error(`[MessageService] Could not mark message ${messageId} as delivered`)
        this.logService.debug(err.message)
        return EMPTY
      })
    )
  }

  markAsRead(messageId: string): Observable<MessageDto> {
    const locationId = this.locationService.location?.id
    if (!locationId) {
      this.logService.error('[MessageService] No location ID available')
      return EMPTY
    }

    const url = `${this.CMN_LOCATION_API_URL}/public/locations/${locationId}/messages/${messageId}/mark-read`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))

    return this.httpClient.put<MessageDto>(url, {}, { headers }).pipe(
      catchError(err => {
        this.logService.error(`[MessageService] Could not mark message ${messageId} as read`)
        this.logService.debug(err.message)
        return EMPTY
      })
    )
  }

  deleteMessage(messageId: string): Observable<void> {
    const locationId = this.locationService.location?.id
    if (!locationId) {
      this.logService.error('[MessageService] No location ID available')
      return EMPTY
    }

    const url = `${this.CMN_LOCATION_API_URL}/public/locations/${locationId}/messages/${messageId}`
    const user = this.stateDataService.deviceId ?? ''
    const headers = new HttpHeaders().set('htv-auth', HttpUtil.getHTVBasicAuth(user))

    return this.httpClient.delete<void>(url, { headers }).pipe(
      catchError(err => {
        this.logService.error(`[MessageService] Could not delete message ${messageId}`)
        this.logService.debug(err.message)
        return EMPTY
      })
    )
  }
}
